/**
 * CalendarApplication.js
 * ----------------------------------------------------------------------------
 * Main entry point for the modular calendar system.
 *
 * Refactor goals:
 * - Fix ErrorHandler construction + API mismatches
 * - Make lifecycle state explicit (initialized vs destroyed)
 * - Keep backward compatible globals (CalendarApp, calendarAPI, calendarApp)
 * - Reduce duplicated global wiring and fragile error access
 */

import CalendarConfig from '../config/CalendarConfig.js';
import DependencyContainer from './DependencyContainer.js';
import ComponentFactory from './ComponentFactory.js';
import CalendarLogger from '../utils/CalendarLogger.js';
import ErrorHandler from '../../utils/ErrorHandler.js';

class CalendarApplication {
  constructor(options = {}) {
    this.config = new CalendarConfig(options);
    this.logger = new CalendarLogger(this.config.debug);

    // FIX: ErrorHandler expects options object, not a logger instance
    this.errorHandler = new ErrorHandler({
      module: 'CalendarApplication',
      maxErrors: 25,
    });

    this.container = new DependencyContainer(this.config, this.logger, this.errorHandler);
    this.factory = new ComponentFactory(this.container);

    this.state = {
      initialized: false,
      destroyed: false,
      components: new Map(),
      eventListeners: [],
    };

    this.initialEventData = null;
    this.renderWhenVisible = null;

    // Pre-init global wiring to stop polling / allow early init calls
    if (this.config.exposeGlobals && typeof window !== 'undefined') {
      this._setupPreInitGlobals();
    }
  }

  /* ------------------------------------------------------------------
   * Public lifecycle
   * ------------------------------------------------------------------ */

  async initialize() {
    if (this.state.destroyed) {
      this.errorHandler.handleError(
        'Attempted to initialize after destruction',
        new Error('CalendarApplication is destroyed'),
        { category: 'lifecycle' }
      );
      return false;
    }

    if (this.state.initialized) {
      this.logger.info('✅ CalendarApplication.initialize() - already initialized');
      return true;
    }

    try {
      this.logger.info('🔧 Starting calendar application...');

      if (!this._validateEnvironment()) {
        return false;
      }

      await this._initializeCore();
      const success = await this._initializeComponents();

      if (success) {
        this.state.initialized = true;
        this._setupPostInitGlobals();
        this.logger.info('🎉 Calendar application initialized successfully');
      }

      return success;
    } catch (error) {
      this.errorHandler.handleFatalError('Application initialization failed', error, {
        category: 'initialization',
      });
      return false;
    }
  }

  async destroy() {
    if (this.state.destroyed) return;

    try {
      this.logger.info('🗑️ Destroying calendar application...');

      // Mark destroyed early to prevent deferred calls
      this.state.destroyed = true;
      this.state.initialized = false;

      // Remove deferred renderer hook
      if (this.config.exposeGlobals && typeof window !== 'undefined') {
        if (window.calendarRenderWhenVisible) delete window.calendarRenderWhenVisible;
      }
      this.renderWhenVisible = null;

      // Clean up event listeners
      this.state.eventListeners.forEach(({ element, event, handler }) => {
        try {
          element.removeEventListener(event, handler);
        } catch (e) {
          // ignore
        }
      });

      // Destroy components
      for (const [name, component] of this.state.components) {
        if (component && typeof component.destroy === 'function') {
          try {
            await component.destroy();
            this.logger.info(`Component ${name} destroyed`);
          } catch (error) {
            this.errorHandler.handleError(`Error destroying component ${name}`, error, {
              category: 'cleanup',
              component: name,
            });
          }
        }
      }

      // Destroy container + factory
      try {
        this.logger.info('🗑️ Destroying dependency container...');
        await this.container.destroy();
        this.logger.info('✅ Dependency container destroyed');
      } catch (error) {
        this.errorHandler.handleError('Error destroying dependency container', error, {
          category: 'cleanup',
        });
      }

      try {
        this.logger.info('🗑️ Destroying ComponentFactory...');
        await this.factory.destroy();
        this.logger.info('✅ ComponentFactory destroyed');
      } catch (error) {
        this.errorHandler.handleError('Error destroying ComponentFactory', error, {
          category: 'cleanup',
        });
      }

      // Remove globals (but avoid deleting if someone else replaced them)
      if (this.config.exposeGlobals && typeof window !== 'undefined') {
        if (window.calendarApp === this) delete window.calendarApp;
        if (window.calendarAPI) delete window.calendarAPI;
        if (window.CalendarApp && window.CalendarApp.app === this) delete window.CalendarApp;
      }

      // Clear state
      this.state.components.clear();
      this.state.eventListeners.length = 0;

      this.logger.info('✅ Calendar application destroyed');
    } catch (error) {
      this.errorHandler.handleError('Error during destruction', error, { category: 'cleanup' });
    }
  }

  /* ------------------------------------------------------------------
   * Validation + core setup
   * ------------------------------------------------------------------ */

  _validateEnvironment() {
    if (typeof document === 'undefined') {
      this.errorHandler.logError('DOM not available', new Error('document is undefined'), {
        category: 'environment',
      });
      return false;
    }

    const calendarEl = document.querySelector(this.config.selectors.calendar);
    if (!calendarEl) {
      this.errorHandler.logError(
        'Calendar container element not found',
        new Error('Missing calendar root element'),
        { category: 'environment', selector: this.config.selectors.calendar }
      );
      return false;
    }

    return true;
  }

  async _initializeCore() {
    this.logger.info('📊 Initializing core dependencies...');
    await this.container.initialize();
    this.factory.initialize();
  }

  async _initializeComponents() {
    try {
      this.logger.info('📅 Initializing calendar components...');

      // Create components through factory
      const eventAdapter = await this.factory.createEventDataAdapter();
      const modalAdapter = await this.factory.createModalAdapter();
      const rendererAdapter = await this.factory.createRendererAdapter(eventAdapter);

      // Store components
      this.state.components.set('eventData', eventAdapter);
      this.state.components.set('modal', modalAdapter);
      this.state.components.set('renderer', rendererAdapter);

      // Initialize calendar through renderer
      const calendar = rendererAdapter.getCalendarInstance();
      this.state.components.set('calendar', calendar);

      // Wire cross-component dependencies via dependency injection
      this._wireComponentDependencies(eventAdapter, modalAdapter, rendererAdapter);

      // Load initial event data if provided
      if (this.initialEventData && Array.isArray(this.initialEventData)) {
        this.logger.info(`📥 Loading ${this.initialEventData.length} initial events into calendar`);
        rendererAdapter.updateEvents(this.initialEventData);
      }

      // Setup event handling
      this._setupEventHandling(eventAdapter, modalAdapter);

      // Conditional rendering
      await this._conditionalRender(rendererAdapter);

      return true;
    } catch (error) {
      this.errorHandler.handleError('Component initialization failed', error, {
        category: 'initialization',
      });
      return false;
    }
  }

  /**
   * Wire cross-component dependencies after creation
   * This replaces the need for BackwardCompatibilityManager globals
   */
  _wireComponentDependencies(eventAdapter, modalAdapter, rendererAdapter) {
    this.logger.info('🔗 Wiring component dependencies...');

    // Get the event data manager from the adapter
    const eventDataManager = eventAdapter?.getEventManager?.();

    // Update ModalAdapter with component refs for EventClickResolver
    if (modalAdapter && typeof modalAdapter.setComponentRefs === 'function') {
      modalAdapter.setComponentRefs({
        calendarApp: this,
        eventDataManager: eventDataManager,
        calendarRenderer: rendererAdapter
      });
      this.logger.info('  ✅ ModalAdapter component refs wired');
    }

    // Update CalendarRenderer with event handlers
    const calendarRenderer = rendererAdapter?.getCalendarRenderer?.();
    if (calendarRenderer && typeof calendarRenderer.setEventHandlers === 'function') {
      calendarRenderer.setEventHandlers({
        modalManager: modalAdapter?.getModalManager?.()
      });
      this.logger.info('  ✅ CalendarRenderer event handlers wired');
    }

    this.logger.info('✅ Component dependencies wired successfully');
  }

  /* ------------------------------------------------------------------
   * Event handling (DOM click fallback)
   * ------------------------------------------------------------------ */

  _setupEventHandling(eventAdapter, modalAdapter) {
    this.logger.info('🖱️ Setting up event handling...');

    const calendarEl = document.querySelector(this.config.selectors.calendar);
    const clickHandler = (e) => this._handleCalendarClick(e, eventAdapter, modalAdapter);

    calendarEl.addEventListener('click', clickHandler);

    this.state.eventListeners.push({
      element: calendarEl,
      event: 'click',
      handler: clickHandler,
    });
  }

  _handleCalendarClick(e, eventAdapter, modalAdapter) {
    // Guard: if destroyed, do nothing
    if (this.state.destroyed) return;

    // NOTE: This is a DOM fallback handler. FullCalendar’s eventClick handler
    // is preferred when available.
    if (!e.target.classList.contains('fc-event-title')) {
      return;
    }

    try {
      const titleText = e.target.textContent.trim();
      const frameEl = e.target.closest(this.config.selectors.eventFrame);
      const dateEl = frameEl?.querySelector(this.config.selectors.eventDate);
      const dateText = dateEl?.textContent.trim() || '';

      const event = eventAdapter.findEventByTitle(titleText);
      if (event && modalAdapter && modalAdapter.isAvailable()) {
        modalAdapter.showEventModal(event, dateText);
      }
    } catch (error) {
      this.errorHandler.handleError('Error in event click handler', error, {
        category: 'interaction',
      });
    }
  }

  /* ------------------------------------------------------------------
   * Rendering (immediate vs deferred)
   * ------------------------------------------------------------------ */

  async _conditionalRender(rendererAdapter) {
    if (!this.config.autoRender) {
      this.logger.info('📅 Auto-render disabled');
      return;
    }

    const calendarSection = document.querySelector(this.config.selectors.calendarSection);
    const shouldRender =
      !calendarSection || !calendarSection.classList.contains(this.config.classes.hide);

    if (shouldRender) {
      this.logger.info('📅 Rendering calendar immediately...');
      const success = await rendererAdapter.render();

      if (success) {
        this.logger.info('✅ Calendar rendered successfully');
      } else {
        this.errorHandler.handleError('Failed to render calendar', new Error('Render failed'), {
          category: 'render',
        });
      }
    } else {
      this.logger.info('📅 Calendar section hidden, will render when visible');
      this._setupDeferredRendering(rendererAdapter);
    }
  }

  _setupDeferredRendering(rendererAdapter) {
    const calendarSection = document.querySelector(this.config.selectors.calendarSection);
    if (!calendarSection) return;

    const renderWhenVisible = async () => {
      // SAFETY CHECK: Don't render if destroyed / not active
      if (this.state.destroyed || !this.state.initialized) {
        this.logger.warn('Attempted to render after destruction, skipping');
        return;
      }

      this.logger.info('📅 Calendar section now visible, rendering...');
      const success = await rendererAdapter.render();

      if (success) {
        this.logger.info('✅ Calendar rendered successfully after becoming visible');

        setTimeout(() => {
          if (this.state.destroyed || !this.state.initialized) return;

          const calendar = rendererAdapter.getCalendarInstance();
          if (calendar && calendar.updateSize) {
            calendar.updateSize();
            this.logger.info('📏 Calendar size updated');
          }
        }, 100);
      }
    };

    this.renderWhenVisible = renderWhenVisible;

    // Expose globally for view toggle scripts
    if (this.config.exposeGlobals && typeof window !== 'undefined') {
      window.calendarRenderWhenVisible = renderWhenVisible;
    }
  }

  /* ------------------------------------------------------------------
   * Globals + public API
   * ------------------------------------------------------------------ */

  _setupPreInitGlobals() {
    // Create CalendarApp early so external code can call init() without polling
    window.CalendarApp = {
      init: async (eventData) => {
        // Allow init calls to provide events
        if (eventData && Array.isArray(eventData)) {
          this.logger.info(`📥 CalendarApp.init() received ${eventData.length} events`);
          this.initialEventData = eventData;

          // If already initialized, apply immediately
          if (this.state.initialized) {
            const renderer = this.getComponent('renderer');
            if (renderer) {
              this.logger.info(`📥 Updating calendar with ${eventData.length} events`);
              renderer.updateEvents(eventData);
              await renderer.render();
            }
            return true;
          }
        }

        return await this.initialize();
      },

      isReady: () => this.state.initialized,
      getInstance: () => this,
      app: this,

      // Convenience methods commonly expected
      render: async () => {
        const renderer = this.getComponent('renderer');
        return renderer ? await renderer.render() : false;
      },
      destroy: () => this.destroy(),
      getCalendar: () => this.getComponent('calendar'),
    };
  }

  _setupPostInitGlobals() {
    if (!this.config.exposeGlobals || typeof window === 'undefined') return;

    // Essential globals for external script compatibility
    window.calendarApp = this;
    window.calendarAPI = this.createPublicAPI();

    // Enhance CalendarApp with post-init helpers
    if (window.CalendarApp) {
      window.CalendarApp.isReady = () => this.state.initialized;
      window.CalendarApp.getInstance = () => this;
      window.CalendarApp.app = this;

      window.CalendarApp.getEvents = () => this.getComponent('eventData')?.getAllEvents() || [];
      window.CalendarApp.showModal = (content) => this.getComponent('modal')?.show(content);
      window.CalendarApp.hideModal = () => this.getComponent('modal')?.hide();
    }

    this.logger.info('✅ Post-init globals set up (calendarApp, calendarAPI)');
  }

  createPublicAPI() {
    const calendar = this.state.components.get('calendar');
    const eventData = this.state.components.get('eventData');
    const modal = this.state.components.get('modal');
    const renderer = this.state.components.get('renderer');

    return {
      // Navigation
      goToDate: (date) => calendar?.gotoDate?.(date),
      today: () => calendar?.today?.(),
      next: () => calendar?.next?.(),
      prev: () => calendar?.prev?.(),

      // View management
      changeView: (view) => calendar?.changeView?.(view),
      render: () => renderer?.render(),

      // Event management
      getEvents: () => eventData?.getAllEvents() || [],
      findEvents: (criteria) => eventData?.findEvents(criteria) || [],
      refreshEvents: () => eventData?.refresh?.(),

      // Modal
      showModal: (content) => modal?.show?.(content),
      hideModal: () => modal?.hide?.(),

      // State
      isInitialized: () => this.state.initialized,
      isDestroyed: () => this.state.destroyed,

      // FIX: ErrorHandler API alignment (no getErrors/getErrorStats methods exist)
      getErrorCount: () => this.errorHandler.getErrorCount(),
      getRecentErrors: (limit = 10) => this.errorHandler.getRecentErrors(limit),

      // Basic stats (prefer eventData stats if present)
      getStats: () => this.getStats(),
    };
  }

  /* ------------------------------------------------------------------
   * Component helpers + stats
   * ------------------------------------------------------------------ */

  getComponent(name) {
    return this.state.components.get(name);
  }

  getComponents() {
    return new Map(this.state.components);
  }

  isInitialized() {
    return this.state.initialized;
  }

  isDestroyed() {
    return this.state.destroyed;
  }

  getStats() {
    const eventData = this.getComponent('eventData');

    return {
      initialized: this.state.initialized,
      destroyed: this.state.destroyed,
      componentCount: this.state.components.size,
      eventListenerCount: this.state.eventListeners.length,
      components: Array.from(this.state.components.keys()),
      containerStats: this.container.getStats?.() || {},
      errors: {
        total: this.errorHandler.getErrorCount(),
        recent: this.errorHandler.getRecentErrors(5),
      },
      eventDataStats: eventData?.getStats?.() || {},
    };
  }
}

export default CalendarApplication;
